import type { GameAction } from "./actions";
import type { InputManager } from "./InputManager";

export interface ActionButtonOptions {
  /** Action engagée tant que le bouton est enfoncé. */
  readonly action: GameAction;
  /** Nom accessible du bouton, et infobulle. */
  readonly label: string;
  /** Glyphe affiché. Purement décoratif : le nom vient de `label`. */
  readonly icon: string;
  /** Élément hôte du widget. `document.body` par défaut. */
  readonly parent?: HTMLElement;
}

/**
 * Réglages du bouton de coupe : le seul rôle que le widget tienne pour
 * l'instant, mais rien dans sa mise en œuvre ne le suppose.
 */
export const CHOP_BUTTON: Omit<ActionButtonOptions, "parent"> = {
  action: "chop",
  label: "Couper l'arbre devant soi",
  icon: "🪓",
};

/**
 * Bouton d'action affiché en bas à droite de l'écran.
 *
 * Il complète le manche unique : celui-ci occupe le pouce gauche pour conduire,
 * celui-ci donne au pouce droit la seule commande qui restait au clavier. Le jeu
 * devient jouable de bout en bout sans clavier, ce qui est la condition pour
 * qu'il le soit sur téléphone.
 *
 * Comme le joystick, il ne connaît ni la scène ni le personnage : il publie une
 * intensité dans l'`InputManager`, et c'est la scène qui décide de ce qu'un
 * appui déclenche. Le suivi passe par les Pointer Events — souris, tactile et
 * stylet empruntent le même chemin de code — et la commande est engagée dès le
 * `pointerdown`, sans attendre le relâchement : un coup de hache doit partir au
 * moment où le pouce se pose.
 */
export class ActionButton {
  /** Racine du widget, montée dans le DOM à la construction. */
  readonly element: HTMLButtonElement;

  private readonly input: InputManager;
  private readonly action: GameAction;

  /** Identifiant du pointeur qui tient le bouton, `null` au repos. */
  private pointerId: number | null = null;

  constructor(input: InputManager, options: ActionButtonOptions) {
    this.input = input;
    this.action = options.action;

    const icon = document.createElement("span");
    icon.className = "action-button__icon";
    icon.textContent = options.icon;
    // Le glyphe redit ce que porte déjà le nom accessible du bouton.
    icon.setAttribute("aria-hidden", "true");

    this.element = document.createElement("button");
    // `type="button"` : sans lui, un bouton placé un jour dans un formulaire le
    // soumettrait au lieu de couper un arbre.
    this.element.type = "button";
    // Le placement est affaire de CSS : le TypeScript n'a pas à connaître de
    // coordonnées.
    this.element.className = "action-button";
    this.element.setAttribute("aria-label", options.label);
    this.element.title = options.label;
    this.element.append(icon);

    (options.parent ?? document.body).append(this.element);

    this.element.addEventListener("pointerdown", this.handlePointerDown);
    // Relâchement écouté au niveau de la fenêtre : un doigt qui glisse hors du
    // bouton avant de se lever laisserait sinon l'action engagée à vie.
    window.addEventListener("pointerup", this.handlePointerUp);
    window.addEventListener("pointercancel", this.handlePointerUp);
    window.addEventListener("blur", this.handleBlur);
    this.element.addEventListener("click", this.handleClick);
  }

  /** Le bouton est-il enfoncé à cet instant ? */
  get isPressed(): boolean {
    return this.pointerId !== null;
  }

  dispose(): void {
    this.release();

    this.element.removeEventListener("pointerdown", this.handlePointerDown);
    window.removeEventListener("pointerup", this.handlePointerUp);
    window.removeEventListener("pointercancel", this.handlePointerUp);
    window.removeEventListener("blur", this.handleBlur);
    this.element.removeEventListener("click", this.handleClick);

    this.element.remove();
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    // Un seul pointeur à la fois : les doigts suivants restent disponibles pour
    // le manche et pour l'orbite caméra.
    if (this.pointerId !== null) return;
    // Clic droit ou molette : rien à faire ici.
    if (event.pointerType === "mouse" && event.button !== 0) return;

    this.pointerId = event.pointerId;
    this.element.classList.add("action-button--active");

    // Empêche la prise de focus, la sélection et les évènements souris de
    // compatibilité que le navigateur synthétiserait après un appui tactile. Le
    // focus resterait sinon sur le bouton, et la caméra ne recevrait plus rien.
    event.preventDefault();

    this.input.setAnalogStrength(this.action, 1);
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.pointerId) return;
    this.release();
  };

  private readonly handleBlur = (): void => {
    this.release();
  };

  /**
   * Activation au clavier (`Entrée` sur un bouton qui a le focus).
   *
   * Un clic issu d'un pointeur a déjà été traité par les deux gestionnaires
   * ci-dessus : le rejouer donnerait deux appuis pour un seul geste. Il se
   * distingue à son compteur de clics, que le navigateur laisse à zéro pour un
   * clic synthétisé par le clavier.
   */
  private readonly handleClick = (event: MouseEvent): void => {
    if (event.detail !== 0) return;

    // Appui et relâchement dans la foulée : l'`InputManager` en tire une
    // transition complète, et la scène le coup de hache qui va avec.
    this.input.setAnalogStrength(this.action, 1);
    this.input.setAnalogStrength(this.action, 0);
  };

  /** Relâche l'action et rend la main au clavier. */
  private release(): void {
    if (this.pointerId === null) return;

    this.pointerId = null;
    this.element.classList.remove("action-button--active");
    this.input.setAnalogStrength(this.action, 0);
  }
}
